<template>
  <EditorPanel
    v-if="channel"
    class="pb-pr-panel"
    :loading="loading"
    @deselect="deselectPreset"
  >
    <template #header>
      <RouterTabList
        v-if="presetRanges && channel.canHavePresets"
        :add-title="translate('pb2.presetsListView.addPresetRangeTooltip')"
        @add="addPresetRange"
      >
        <RouterTabItem
          v-for="iteratedPresetRange in presetRanges"
          :key="iteratedPresetRange.data._uid"
          :to="iteratedPresetRange.link"
          :title="iteratedPresetRange.linkName"
        >
          {{ iteratedPresetRange.linkName }}
          <ReportIcon
            class="pb-preset-range-item-report"
            :report="iteratedPresetRange.report"
          />
          <LinkIcon
            v-bind="iteratedPresetRange.data"
          />
        </RouterTabItem>
      </RouterTabList>
    </template>

    <template v-if="presetRange">
      <PresetRange v-if="presets && presets.length" />
      <PanelMessage v-else>
        {{ translate('pb2.presetsListView.noPresetsMessage') }}
        <ControlButton
          class="btn-success"
          :label="translate('pb2.presetsListView.addPresetsLabel')"
          :disabled="readonly"
          :to="addPresetLink"
        />
      </PanelMessage>
    </template>
    <PanelMessage v-else>
      <template v-if="channel.canHavePresets">
        {{ translate('pb2.presetsListView.noPresetRangesMessage') }}
        <ButtonWithLoader
          class="btn-success"
          :label="translate('pb2.presetsListView.addPresetRangeLabel')"
          :disabled="readonly"
          @click="addPresetRange"
        />
      </template>
      <template v-else-if="channel.data.lsb">
        {{ translate('pb2.presetsListView.editMsbChannelMessage') }}
        <PbLink :to="channel.msb.link">
          {{ translate('pb2.presetsListView.editMsbChannelLink') }}
        </PbLink>
      </template>
      <template v-else>
        {{ translate('pb2.presetsListView.presetsNotAcceptedMessage') }}
      </template>
    </PanelMessage>

    <template
      v-if="presetRange && channel.canHavePresets"
      #footer
    >
      <ButtonGroup
        row
        padded
      >
        <ControlButton
          :to="addPresetLink"
          :disabled="readonly"
        >
          <span class="ellipsis">{{ translate('pb2.presetsListView.addPresetsLabel') }}</span>
        </ControlButton>
        <ControlButton
          :to="distributeLink"
          :disabled="readonly || !hasPresets"
        >
          <span class="ellipsis">{{ translate('pb2.presetsListView.distributePresetsLabel') }}</span>
        </ControlButton>
      </ButtonGroup>
    </template>
  </EditorPanel>
  <EmptyPanel
    v-else
    message="pb2.panelMessages.selectOrAddChannel"
  />
</template>

<script setup>
import { computed } from 'vue'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import EmptyPanel from '@/views/ProfileEditor/ProfileContent/panels/EmptyPanel.vue'
import routeNames from '@/router/names'
import useRouterStore from '@/stores/router.store'
import usePresetRangeStore from '@/stores/presetRange.store'
import useProfileStore from '@/stores/profile.store'
import RouterTabList from '@/components/RouterTabs/RouterTabList.vue'
import RouterTabItem from '@/components/RouterTabs/RouterTabItem.vue'
import PresetRange from '@/components/PresetRange/PresetRange.vue'
import EditorPanel from '@/components/EditorPanel/EditorPanel.vue'
import ReportIcon from '@/components/ItemIcons/ReportIcon.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue'
import PbLink from '@/components/PbLink.vue'
import PanelMessage from '@/components/PanelMessage/PanelMessage.vue'
import LinkIcon from '@/components/ItemIcons/LinkIcon.vue'
import { useCurrent, usePresetRangeStatus, useReadonly } from '@/composables/useProfile'
import useIsGlobal from '@/composables/useIsGlobal'
import useDestination from '@/composables/useDestination'
import useTranslate from '@/composables/useTranslate'

const { isGlobal } = useIsGlobal()

const { translate } = useTranslate()

const presetRangeStore = usePresetRangeStore()
const profileStore = useProfileStore()
const routerStore = useRouterStore()

const {
  channel,
  presetRanges,
  presetRange,
  presets,
} = useCurrent()
const readonly = useReadonly()

const loading = usePresetRangeStatus().pending

const hasPresets = computed(() => presets.value && presets.value.length > 1)

const distributeLink = computed(() => {
  const global = isGlobal.value
  const currentRoute = routerStore.route
  const {
    profileUid,
    modeUid,
    channelUid,
    presetRangeUid,
    presetUid,
  } = profileStore
  if (readonly.value || !hasPresets.value) {
    return currentRoute
  }
  const params = {
    profileUid,
    channelUid,
    presetRangeUid,
  }
  if (!global) {
    params.modeUid = modeUid
  }
  return {
    name: global ? routeNames.DistributeGlobalPresets : routeNames.DistributeLocalPresets,
    params,
    // Move the required params to query parameters,
    // so that they're accessible in the dialog
    query: {
      presetUid,
    },
  }
})

const addPresetLink = computed(() => {
  const global = isGlobal.value
  const currentRoute = routerStore.route
  const {
    profileUid,
    modeUid,
    channelUid,
    presetRangeUid,
    presetUid,
  } = profileStore
  if (readonly.value) {
    return currentRoute
  }
  const params = {
    profileUid,
    channelUid,
    presetRangeUid,
  }
  if (!global) {
    params.modeUid = modeUid
  }
  return {
    name: global ? routeNames.AddGlobalPreset : routeNames.AddLocalPreset,
    params,
    // Move the required params to query parameters,
    // so that they're accessible in the dialog
    query: {
      presetUid,
    },
  }
})

function addPresetRange() {
  if (isGlobal.value) {
    return presetRangeStore.createGlobal()
  }
  return presetRangeStore.createLocal()
}

const deselectPresetDestination = useDestination(computed(() => ({
  name: isGlobal.value ? routeNames.GlobalPresetRange : routeNames.LocalPresetRange,
})))

function deselectPreset() {
  return routerStore.push(deselectPresetDestination.value)
}
</script>
